/* ─────────────────────────────────────────────────────────────────────
   MLW Interactions — vanilla JS, zero dependencies.

   Modules:
     1. Hamburger menu toggle  (with focus trap, scroll lock, Escape)
     2. Cart drawer toggle     (bridges to Dawn's <cart-drawer> element)
     3. FAQ accordion          (one-at-a-time per group, event delegation)
     4. Subscribe & Save       (syncs hidden [name=selling_plan], dispatches event)
     5. Countdown timer        (duration OR ISO end-time, setInterval)
     6. Sticky CTA bar         (IntersectionObserver on trigger element)
     7. Image gallery          (thumbnail click → swaps main image, event delegation)

   Hooks set up in Session B snippets:
     [data-mlw-open="hamburger|cart"]      — trigger buttons
     [data-mlw-close="hamburger|cart"]     — close buttons / overlay
     [data-mlw-drawer="hamburger"]         — hamburger panel root
     [data-mlw-faq-item] / [data-mlw-faq-toggle]
     [data-mlw-subtoggle] / [data-mlw-subtoggle-switch] / [data-mlw-subtoggle-input]
     [data-mlw-countdown]
     [data-mlw-sticky-cta] / [data-mlw-sticky-trigger]
     [data-mlw-gallery] / [data-mlw-thumb] / [data-mlw-main-image]
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.__mlwInteractions) return;
  window.__mlwInteractions = true;

  // ────────────────────────────────────────────────────────────────────
  // Shared utilities
  // ────────────────────────────────────────────────────────────────────

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var scrollLock = {
    count: 0,
    enable: function () {
      if (this.count === 0) {
        document.documentElement.classList.add('mlw-scroll-locked');
        document.body.classList.add('mlw-scroll-locked');
      }
      this.count++;
    },
    disable: function () {
      this.count = Math.max(0, this.count - 1);
      if (this.count === 0) {
        document.documentElement.classList.remove('mlw-scroll-locked');
        document.body.classList.remove('mlw-scroll-locked');
      }
    }
  };

  function focusFirst(container) {
    var el = container.querySelector(FOCUSABLE);
    if (el) el.focus();
  }

  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    var focusables = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
    if (!focusables.length) { e.preventDefault(); return; }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  // ────────────────────────────────────────────────────────────────────
  // Module 1: Hamburger menu
  // ────────────────────────────────────────────────────────────────────

  var hamburger = {
    panel: null,
    lastTrigger: null,
    keyHandler: null,

    get isOpen() { return this.panel && this.panel.getAttribute('data-open') === 'true'; },

    open: function (trigger) {
      this.panel = document.querySelector('[data-mlw-drawer="hamburger"]');
      if (!this.panel || this.isOpen) return;
      this.lastTrigger = trigger || document.activeElement;
      this.panel.setAttribute('data-open', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mlw-menu-open');
      document.querySelectorAll('[data-mlw-open="hamburger"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'true');
      });
      scrollLock.enable();
      var self = this;
      setTimeout(function () { focusFirst(self.panel); }, 50);
      this.keyHandler = function (e) {
        if (e.key === 'Escape') { self.close(); return; }
        trapFocus(self.panel, e);
      };
      document.addEventListener('keydown', this.keyHandler);
    },

    close: function () {
      if (!this.panel || !this.isOpen) return;
      this.panel.setAttribute('data-open', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mlw-menu-open');
      document.querySelectorAll('[data-mlw-open="hamburger"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      scrollLock.disable();
      if (this.keyHandler) {
        document.removeEventListener('keydown', this.keyHandler);
        this.keyHandler = null;
      }
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Module 2: Cart drawer — drives sections/mlw-cart-drawer.liquid
  // ────────────────────────────────────────────────────────────────────

  var cartDrawer = {
    get panel() { return document.querySelector('[data-mlw-drawer="cart"]'); },
    get isOpen() { var p = this.panel; return !!p && p.getAttribute('data-open') === 'true'; },

    open: function (trigger) {
      var panel = this.panel;
      if (panel) {
        if (this.isOpen) return;
        panel.__mlwTrigger = trigger || document.activeElement;
        panel.setAttribute('data-open', 'true');
        panel.setAttribute('aria-hidden', 'false');
        scrollLock.enable();
        setTimeout(function () {
          var closeBtn = panel.querySelector('button.mlw-cart-drawer__close');
          if (closeBtn) closeBtn.focus();
        }, 60);
        return;
      }
      // Fallback only when the MLW drawer section isn't on the page.
      var dawn = document.querySelector('cart-drawer');
      if (dawn && typeof dawn.open === 'function') {
        try { dawn.open(trigger); } catch (err) { dawn.open(); }
        return;
      }
      var notif = document.querySelector('cart-notification');
      if (notif && typeof notif.open === 'function') notif.open();
    },
    close: function () {
      var panel = this.panel;
      if (panel) {
        if (!this.isOpen) return;
        panel.setAttribute('data-open', 'false');
        panel.setAttribute('aria-hidden', 'true');
        scrollLock.disable();
        if (panel.__mlwTrigger && typeof panel.__mlwTrigger.focus === 'function') {
          panel.__mlwTrigger.focus();
        }
        return;
      }
      var dawn = document.querySelector('cart-drawer');
      if (dawn && typeof dawn.close === 'function') { dawn.close(); return; }
      var notif = document.querySelector('cart-notification');
      if (notif && typeof notif.close === 'function') notif.close();
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Module 3: FAQ accordion (event delegation, one-at-a-time per group)
  // ────────────────────────────────────────────────────────────────────

  function faqToggle(btn) {
    var item = btn.closest('[data-mlw-faq-item]');
    if (!item) return;
    var wasOpen = item.getAttribute('data-open') === 'true';

    // Close siblings in same group (matches Eliza's one-at-a-time JSX pattern).
    // Group scope: closest [data-mlw-faq-group], else document.
    var group = item.closest('[data-mlw-faq-group]') || document;
    if (!wasOpen) {
      group.querySelectorAll('[data-mlw-faq-item][data-open="true"]').forEach(function (other) {
        if (other === item) return;
        other.setAttribute('data-open', 'false');
        var otherBtn = other.querySelector('[data-mlw-faq-toggle]');
        if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
      });
    }

    item.setAttribute('data-open', wasOpen ? 'false' : 'true');
    btn.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
  }

  // ────────────────────────────────────────────────────────────────────
  // Module 4: Subscribe & Save toggle
  // ────────────────────────────────────────────────────────────────────

  function bindSubToggle(root) {
    if (root.__mlwBound) return;
    root.__mlwBound = true;

    var btn = root.querySelector('[data-mlw-subtoggle-switch]');
    var input = root.querySelector('[data-mlw-subtoggle-input]');
    if (!btn || !input) return;

    var planId = root.getAttribute('data-selling-plan') || '';

    function setMode(mode) {
      root.setAttribute('data-mode', mode);
      var on = mode === 'subscription';
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      input.value = on ? planId : '';
      root.dispatchEvent(new CustomEvent('mlw:subtoggle:change', {
        bubbles: true,
        detail: { mode: mode, sellingPlan: on ? planId : '' }
      }));
    }

    btn.addEventListener('click', function () {
      setMode(root.getAttribute('data-mode') === 'subscription' ? 'onetime' : 'subscription');
    });
  }

  function initSubToggles(scope) {
    (scope || document).querySelectorAll('[data-mlw-subtoggle]').forEach(bindSubToggle);
  }

  // ────────────────────────────────────────────────────────────────────
  // Module 5: Countdown timer
  // ────────────────────────────────────────────────────────────────────

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function bindCountdown(el) {
    if (el.__mlwBound) return;
    el.__mlwBound = true;

    var endAttr = el.getAttribute('data-end-time');
    var target;
    if (endAttr) {
      target = new Date(endAttr).getTime();
    } else {
      var h = parseInt(el.getAttribute('data-hours'), 10) || 0;
      var m = parseInt(el.getAttribute('data-minutes'), 10) || 0;
      var s = parseInt(el.getAttribute('data-seconds'), 10) || 0;
      target = Date.now() + ((h * 3600) + (m * 60) + s) * 1000;
    }

    var interval = null;
    function tick() {
      var diff = Math.max(0, target - Date.now());
      var total = Math.floor(diff / 1000);
      var hh = Math.floor(total / 3600);
      var mm = Math.floor((total % 3600) / 60);
      var ss = total % 60;
      el.textContent = pad(hh) + 'h : ' + pad(mm) + 'm : ' + pad(ss) + 's';
      if (diff <= 0 && interval) {
        clearInterval(interval);
        el.setAttribute('data-expired', 'true');
        el.dispatchEvent(new CustomEvent('mlw:countdown:expired', { bubbles: true }));
      }
    }
    tick();
    interval = setInterval(tick, 1000);
    el.__mlwCountdownInterval = interval;
  }

  function initCountdowns(scope) {
    (scope || document).querySelectorAll('[data-mlw-countdown]').forEach(bindCountdown);
  }

  // ────────────────────────────────────────────────────────────────────
  // Module 6: Sticky CTA bar (IntersectionObserver)
  // ────────────────────────────────────────────────────────────────────

  function bindStickyCta(bar) {
    if (bar.__mlwBound) return;
    bar.__mlwBound = true;

    var triggerSelector = bar.getAttribute('data-mlw-sticky-trigger-selector') || '[data-mlw-sticky-trigger]';
    var trigger = document.querySelector(triggerSelector);
    if (!trigger) {
      // No trigger — behave as always-visible once page settled.
      bar.classList.add('is-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show only when trigger has scrolled OFF the top of the viewport.
        var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', scrolledPast);
      });
    }, { threshold: 0, rootMargin: '0px 0px 0px 0px' });

    observer.observe(trigger);
    bar.__mlwObserver = observer;
  }

  function initStickyCtas(scope) {
    (scope || document).querySelectorAll('[data-mlw-sticky-cta]').forEach(bindStickyCta);
  }

  // ────────────────────────────────────────────────────────────────────
  // Module 7: Image gallery (event delegation)
  // ────────────────────────────────────────────────────────────────────

  function selectThumb(thumb) {
    var gallery = thumb.closest('[data-mlw-gallery]');
    if (!gallery) return;

    var main = gallery.querySelector('[data-mlw-main-image]');
    var thumbImg = thumb.querySelector('img');
    var src = thumb.getAttribute('data-image-src') || (thumbImg && thumbImg.getAttribute('src'));
    var srcset = thumb.getAttribute('data-image-srcset');
    var alt = thumb.getAttribute('data-image-alt') || (thumbImg && thumbImg.getAttribute('alt'));

    if (main && src) {
      if (main.tagName === 'IMG') {
        main.src = src;
        if (srcset) main.srcset = srcset;
        if (alt) main.alt = alt;
      } else {
        var img = main.querySelector('img');
        if (img) {
          img.src = src;
          if (srcset) img.srcset = srcset;
          if (alt) img.alt = alt;
        } else {
          main.style.backgroundImage = 'url(' + src + ')';
        }
      }
    }

    gallery.querySelectorAll('[data-mlw-thumb]').forEach(function (t) {
      t.classList.toggle('is-active', t === thumb);
      t.setAttribute('aria-selected', t === thumb ? 'true' : 'false');
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // Global event delegation — hamburger open/close, cart, FAQ, gallery
  // ────────────────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var target = e.target;

    var openBtn = target.closest('[data-mlw-open]');
    if (openBtn) {
      var openTarget = openBtn.getAttribute('data-mlw-open');
      if (openTarget === 'hamburger') { e.preventDefault(); hamburger.open(openBtn); return; }
      if (openTarget === 'cart') { e.preventDefault(); cartDrawer.open(openBtn); return; }
    }

    var closeBtn = target.closest('[data-mlw-close]');
    if (closeBtn) {
      var closeTarget = closeBtn.getAttribute('data-mlw-close');
      // If the close trigger is an anchor pointing somewhere real, close the
      // drawer but let the browser navigate. Without this, the empty-cart
      // "Shop the Starter Bundle" CTA dead-clicks because preventDefault
      // cancels the link.
      var closeHref = closeBtn.tagName === 'A' ? closeBtn.getAttribute('href') : null;
      var navigates = closeHref && closeHref !== '' && closeHref !== '#';
      if (closeTarget === 'hamburger') {
        if (!navigates) e.preventDefault();
        hamburger.close();
        return;
      }
      if (closeTarget === 'cart') {
        if (!navigates) e.preventDefault();
        cartDrawer.close();
        return;
      }
    }

    // Intercept any anchor pointing to the cart page — always open the drawer
    // instead of navigating. Catches Dawn's built-in cart-notification "View
    // cart" link, hardcoded header/footer /cart links, etc. Skip modified
    // clicks so ctrl/cmd/shift/middle-click can still open a new tab.
    var anchor = target.closest('a[href]');
    if (anchor && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      try {
        var url = new URL(anchor.href, window.location.href);
        if (url.origin === window.location.origin &&
            url.pathname.replace(/\/$/, '') === '/cart') {
          e.preventDefault();
          cartDrawer.open(anchor);
          return;
        }
      } catch (err) { /* invalid URL — ignore */ }
    }

    var faqBtn = target.closest('[data-mlw-faq-toggle]');
    if (faqBtn) { faqToggle(faqBtn); return; }

    var thumb = target.closest('[data-mlw-thumb]');
    if (thumb && thumb.closest('[data-mlw-gallery]')) { e.preventDefault(); selectThumb(thumb); return; }
  });

  // Escape closes the cart drawer through the same path that manages
  // scroll-lock — keeps the shared counter consistent across open/close cycles.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && cartDrawer.isOpen) cartDrawer.close();
  });

  // ────────────────────────────────────────────────────────────────────
  // Init + Theme Editor re-init hook
  // ────────────────────────────────────────────────────────────────────

  function initAll(scope) {
    initSubToggles(scope);
    initCountdowns(scope);
    initStickyCtas(scope);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    // Scroll-lock CSS — injected once so snippets don't need to duplicate it.
    if (!document.getElementById('mlw-interactions-style')) {
      var style = document.createElement('style');
      style.id = 'mlw-interactions-style';
      style.textContent = 'html.mlw-scroll-locked, body.mlw-scroll-locked { overflow: hidden !important; }' +
        '[data-mlw-sticky-cta] { position: fixed; left: 0; right: 0; bottom: 0; z-index: 95; transform: translateY(100%); transition: transform 0.3s ease; }' +
        '[data-mlw-sticky-cta].is-visible { transform: translateY(0); }' +
        '[data-mlw-thumb] { cursor: pointer; transition: border-color 0.2s ease; }' +
        '[data-mlw-thumb].is-active { border-color: var(--mlw-accent, #8b7fc7) !important; }';
      document.head.appendChild(style);
    }
    initAll(document);
  });

  // Shopify Theme Editor fires these when a section is added / reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    // Clean up intervals for countdowns that are being removed.
    e.target.querySelectorAll('[data-mlw-countdown]').forEach(function (el) {
      if (el.__mlwCountdownInterval) clearInterval(el.__mlwCountdownInterval);
    });
    e.target.querySelectorAll('[data-mlw-sticky-cta]').forEach(function (el) {
      if (el.__mlwObserver) el.__mlwObserver.disconnect();
    });
  });

  // Expose a small debug handle for Session D+ sections that need to trigger things.
  window.MLW = window.MLW || {};
  window.MLW.hamburger = hamburger;
  window.MLW.cart = cartDrawer;
  window.MLW.init = initAll;
})();
